import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import CrewSeederRepairPanel from "@/components/CrewSeederRepairPanel";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const FILTERS = [
  { key: "all", label: "All users", testId: "filter-all" },
  { key: "orphans", label: "Missing referrer", testId: "filter-orphans" },
  { key: "recovered", label: "Attributed", testId: "filter-recovered" },
  { key: "repaired", label: "Admin-repaired", testId: "filter-repaired" },
];

const confidenceBadge = (c) => {
  const map = {
    high: { cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40", label: "High confidence" },
    medium: { cls: "bg-amber-500/20 text-amber-200 border-amber-500/40", label: "Medium" },
    low: { cls: "bg-rose-500/20 text-rose-200 border-rose-500/40", label: "Low — review" },
  };
  const s = map[c] || map.low;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(d).slice(0, 10);
  }
};

const AdminReferralsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("orphans");
  const [totalOrphans, setTotalOrphans] = useState(0);
  const [auditByUser, setAuditByUser] = useState({});
  const [diagnoseByUser, setDiagnoseByUser] = useState({});

  // Assign modal
  const [assignTarget, setAssignTarget] = useState(null);
  const [refInput, setRefInput] = useState("");
  const [reason, setReason] = useState("");
  const [forceReassign, setForceReassign] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // Phase 15.5 P5 Batch F — autocomplete referrer picker
  const [referrerSearch, setReferrerSearch] = useState("");
  const [referrerResults, setReferrerResults] = useState([]);
  const [referrerSearchLoading, setReferrerSearchLoading] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);

  // Auto-recovery
  const [proposals, setProposals] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanRan, setScanRan] = useState(false);

  // Phase 15.5 P5 Batch C — Dashboard counters
  const [dash, setDash] = useState(null);
  // Phase 15.5 P6 — Rewards stats
  const [rewardStats, setRewardStats] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/admin/referrals/dashboard");
        setDash(r.data);
      } catch (_e) { /* non-blocking */ }
      try {
        const rs = await api.get("/admin/rewards/stats");
        setRewardStats(rs.data);
      } catch (_e) { /* non-blocking */ }
    })();
  }, [user]);

  const backfillReferralRewards = async () => {
    if (!window.confirm(
      "Backfill referral signup rewards for every attributed user?\n\n" +
      "Idempotent: users who already received their +100 coin reward are skipped."
    )) return;
    setBackfilling(true);
    try {
      const r = await api.post("/admin/rewards/backfill-referrals");
      toast.success(`Granted ${r.data.granted} referral rewards (+${r.data.total_coins_paid} coins). Skipped ${r.data.skipped} already-rewarded.`);
      const rs = await api.get("/admin/rewards/stats");
      setRewardStats(rs.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const backfillCrewSeedRewards = async () => {
    if (!window.confirm(
      "Backfill crew seeder rewards for every credited seed?\n\n" +
      "Includes +50 coins per seed, +250 badge bonus when threshold hit, and 10/25/50 crew milestone bonuses for crew creators. Idempotent."
    )) return;
    setBackfilling(true);
    try {
      const r = await api.post("/admin/rewards/backfill-crew-seeds");
      toast.success(`Granted ${r.data.granted} seed rewards · ${r.data.badge_grants} badge bonuses · ${r.data.milestone_grants} milestones (+${r.data.total_coins_paid} coins)`);
      const rs = await api.get("/admin/rewards/stats");
      setRewardStats(rs.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/referrals/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rgm-referrals.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!me.data || (me.data.role !== "admin" && me.data.role !== "moderator")) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        setUser(me.data);
      } catch (_e) {
        navigate("/login");
      }
    })();
  }, [navigate]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/referrals/search", {
        params: { q: query, filter, limit: 100 },
      });
      setRows(r.data.rows || []);
      setTotalOrphans(r.data.total_orphans || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [query, filter]);

  useEffect(() => {
    if (user) fetchRows();
  }, [user, fetchRows]);

  const openAssign = (row) => {
    setAssignTarget(row);
    setRefInput("");
    setReason("");
    setForceReassign(!!row.referred_by);
    // Reset autocomplete state
    setReferrerSearch("");
    setReferrerResults([]);
    setSelectedReferrer(null);
  };

  // Phase 15.5 P5 Batch F — debounced autocomplete against /admin/referrals/search
  useEffect(() => {
    if (!assignTarget) return;
    const q = referrerSearch.trim();
    // Stop if user explicitly picked a result
    if (selectedReferrer && q === (selectedReferrer.artist_name || "")) return;
    if (!q || q.length < 2) {
      setReferrerResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setReferrerSearchLoading(true);
      try {
        const r = await api.get("/admin/referrals/search", {
          params: { q, filter: "all", limit: 8 },
        });
        if (cancelled) return;
        // Exclude the target user from autocomplete (no self-referral)
        const rows = (r.data.rows || []).filter(
          (u) => u.user_id !== assignTarget.user_id
        );
        setReferrerResults(rows);
      } catch {
        if (!cancelled) setReferrerResults([]);
      } finally {
        if (!cancelled) setReferrerSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrerSearch, assignTarget?.user_id]);

  const pickReferrer = async (u) => {
    setSelectedReferrer(u);
    setReferrerSearch(u.artist_name || "");
    setReferrerResults([]);
    // Auto-fill the power-user input with the resolved user_id so submit
    // works whether the admin clicks Save or hits Enter.
    setRefInput(u.user_id);
  };

  const clearPickedReferrer = () => {
    setSelectedReferrer(null);
    setReferrerSearch("");
    setRefInput("");
  };

  const submitAssign = async () => {
    if (!assignTarget) return;
    // Phase 15.5 P5 Batch F — prefer the picked autocomplete user; otherwise
    // fall back to the power-user text input (referral_code OR user_id).
    let body = {
      user_id: assignTarget.user_id,
      reason: reason.trim() || null,
      force_reassign: forceReassign,
    };
    if (selectedReferrer && selectedReferrer.user_id) {
      body.referrer_user_id = selectedReferrer.user_id;
    } else {
      const v = refInput.trim();
      if (!v) { toast.error("Search for an artist OR paste a referral_code / user_id"); return; }
      if (v.startsWith("user_") || v.length > 16) body.referrer_user_id = v;
      else body.referrer_code = v;
    }
    setAssigning(true);
    try {
      const r = await api.post("/admin/referrals/assign", body);
      toast.success(`Attributed to ${r.data.new_referrer_name}`);
      setAssignTarget(null);
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assignment failed");
    } finally {
      setAssigning(false);
    }
  };

  const toggleAudit = async (userId) => {
    if (auditByUser[userId]) {
      setAuditByUser((s) => { const c = { ...s }; delete c[userId]; return c; });
      return;
    }
    try {
      const r = await api.get(`/admin/referrals/audit/${userId}`);
      setAuditByUser((s) => ({ ...s, [userId]: r.data.audit || [] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load audit");
    }
  };

  const toggleDiagnose = async (userId) => {
    if (diagnoseByUser[userId]) {
      setDiagnoseByUser((s) => { const c = { ...s }; delete c[userId]; return c; });
      return;
    }
    try {
      const r = await api.get(`/admin/referrals/diagnose/${userId}`);
      setDiagnoseByUser((s) => ({ ...s, [userId]: r.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not diagnose");
    }
  };

  const runScan = async () => {
    setScanLoading(true);
    try {
      const r = await api.get("/admin/referrals/auto-recovery-scan", { params: { limit: 100 } });
      setProposals(r.data.proposals || []);
      setScanRan(true);
      toast.success(`Scan complete: ${r.data.matched} match(es) found from ${r.data.scanned} orphans`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  const bulkApply = async () => {
    if (!window.confirm(
      "Bulk-apply auto-recovery matches with at least MEDIUM confidence?\n\n" +
      "This will assign referrers to every matching orphan in one sweep. " +
      "It's safe — already-attributed users are skipped and every assignment writes an audit row."
    )) return;
    setScanLoading(true);
    try {
      const r = await api.post("/admin/referrals/bulk-apply-recovery", null, {
        params: { min_confidence: "medium", limit: 200 },
      });
      toast.success(`Repaired ${r.data.applied_count} referrals (skipped ${r.data.skipped_count})`);
      setProposals([]);
      setScanRan(true);
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk apply failed");
    } finally {
      setScanLoading(false);
    }
  };

  const acceptProposal = async (p) => {
    try {
      await api.post("/admin/referrals/assign", {
        user_id: p.user.user_id,
        referrer_user_id: p.proposed_referrer.user_id,
        reason: `Auto-recovery scan (${p.confidence} confidence, ${p.proposed_referrer.click_count_in_window} click(s) in window)`,
        force_reassign: false,
      });
      toast.success("Attribution applied");
      setProposals((prev) => prev.filter((x) => x.user.user_id !== p.user.user_id));
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-referrals-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-white">Referral Management</h1>
          <p className="text-gray-400 text-sm mt-1">
            Recover lost referrals, repair attribution, and audit who-credited-who.
          </p>
        </header>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Card className="p-4 bg-gradient-to-br from-rose-900/30 to-rose-800/10 border-rose-500/30" data-testid="stat-orphans">
            <div className="text-xs text-rose-200/80">Missing referrer</div>
            <div className="text-2xl font-bold text-white">{dash?.total_orphans ?? totalOrphans}</div>
            {dash && (
              <div className="text-[10px] text-rose-200/60 mt-1">
                {dash.attribution_rate}% attributed
              </div>
            )}
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-900/30 to-emerald-800/10 border-emerald-500/30" data-testid="stat-proposals">
            <div className="text-xs text-emerald-200/80">Auto-recovery matches</div>
            <div className="text-2xl font-bold text-white">{scanRan ? proposals.length : "—"}</div>
            {dash && (
              <div className="text-[10px] text-emerald-200/60 mt-1">
                {dash.clicks_7d} clicks tracked (7d)
              </div>
            )}
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-900/30 to-purple-800/10 border-purple-500/30" data-testid="stat-repaired">
            <div className="text-xs text-purple-200/80">Admin-repaired</div>
            <div className="text-2xl font-bold text-white">{dash?.total_repaired ?? "—"}</div>
            {dash && (
              <div className="text-[10px] text-purple-200/60 mt-1">
                {dash.recent_referred_30d}/{dash.recent_signups_30d} signups in 30d
              </div>
            )}
          </Card>
          <Card className="p-4 bg-gradient-to-br from-amber-900/30 to-amber-800/10 border-amber-500/30">
            <div className="text-xs text-amber-200/80">Actions</div>
            <div className="flex flex-col gap-1.5 mt-1">
              <Button
                size="sm"
                onClick={runScan}
                disabled={scanLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="run-auto-recovery"
              >
                {scanLoading ? "Scanning…" : "Auto-recovery scan"}
              </Button>
              <Button
                size="sm"
                onClick={bulkApply}
                disabled={scanLoading}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                data-testid="bulk-apply-recovery"
                title="Re-scan AND apply every match with ≥medium confidence in one sweep"
              >
                ⚡ Bulk repair all
              </Button>
              <Button
                size="sm"
                onClick={exportCsv}
                variant="outline"
                className="w-full border-amber-500/40 text-amber-200"
                data-testid="export-csv"
              >
                Export CSV
              </Button>
            </div>
          </Card>
        </div>

        {/* Top referrers (Batch C) */}
        {dash?.top_referrers?.length > 0 && (
          <Card className="mb-6 p-4 bg-gray-900/40 border-gray-800" data-testid="top-referrers-card">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-white font-bold">Top recruiters (all-time)</h2>
              <Link to="/referrals/leaderboard" className="text-xs text-purple-400 hover:text-purple-300">
                View public leaderboard →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              {dash.top_referrers.map((tr, i) => (
                <Link
                  key={tr.user_id}
                  to={`/artist/${tr.user_id}`}
                  className="flex items-center gap-2 p-2 rounded-lg bg-black/40 border border-gray-800 hover:border-purple-500/60 transition-colors"
                  data-testid={`top-referrer-${i + 1}`}
                >
                  <img
                    src={getProfilePhotoUrl(tr)}
                    alt={tr.artist_name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{tr.artist_name}</div>
                    <div className="text-[11px] text-emerald-300">{tr.referral_count} referrals</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* Phase 15.5 P6 — Rewards System overview + backfill controls */}
        <Card
          className="mb-6 p-4 bg-gradient-to-br from-amber-900/20 to-yellow-800/5 border-amber-500/30"
          data-testid="rewards-overview-card"
        >
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-white font-bold flex items-center gap-2">
              🪙 Reward Coins
              {rewardStats && (
                <span className="text-xs text-amber-200/70 font-normal">
                  · {rewardStats.total_paid_coins?.toLocaleString() || 0} coins paid out across {rewardStats.total_grants} grants
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={backfillReferralRewards}
                disabled={backfilling}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="backfill-referral-rewards"
                title={`Grants +${rewardStats?.amounts_config?.referral_signup ?? 100} coins per missing referral`}
              >
                {backfilling ? "…" : "Backfill referral rewards"}
              </Button>
              <Button
                size="sm"
                onClick={backfillCrewSeedRewards}
                disabled={backfilling}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                data-testid="backfill-crew-seed-rewards"
                title={`+${rewardStats?.amounts_config?.crew_seed_join ?? 50} per seed, +${rewardStats?.amounts_config?.crew_seeder_badge_unlocked ?? 250} badge bonus, +10/25/50 crew milestones`}
              >
                {backfilling ? "…" : "Backfill crew seed rewards"}
              </Button>
            </div>
          </div>
          {rewardStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="p-2 rounded-md bg-black/30 border border-amber-500/20" data-testid="reward-missing-referrals">
                <div className="text-amber-200/70">Missing referral rewards</div>
                <div className="text-lg font-bold text-white">{rewardStats.missing_referral_signup_count}</div>
              </div>
              <div className="p-2 rounded-md bg-black/30 border border-emerald-500/20" data-testid="reward-missing-seeds">
                <div className="text-emerald-200/70">Missing crew seed rewards</div>
                <div className="text-lg font-bold text-white">{rewardStats.missing_crew_seed_count}</div>
              </div>
              <div className="p-2 rounded-md bg-black/30 border border-purple-500/20">
                <div className="text-purple-200/70">Granted referrals (+{rewardStats.amounts_config?.referral_signup ?? 100} each)</div>
                <div className="text-lg font-bold text-white">{rewardStats.by_type?.referral_signup?.count || 0}</div>
              </div>
              <div className="p-2 rounded-md bg-black/30 border border-pink-500/20">
                <div className="text-pink-200/70">Granted crew seeds (+{rewardStats.amounts_config?.crew_seed_join ?? 50} each)</div>
                <div className="text-lg font-bold text-white">{rewardStats.by_type?.crew_seed_join?.count || 0}</div>
              </div>
            </div>
          )}
        </Card>

        {/* Phase 15.5 P5 Batch F — Crew Seeder repair panel */}
        <CrewSeederRepairPanel />

        {/* Proposals */}
        {scanRan && proposals.length > 0 && (
          <Card className="mb-6 p-4 bg-gray-900/40 border-emerald-500/40" data-testid="proposals-card">
            <h2 className="text-white font-bold mb-3">Proposed matches ({proposals.length})</h2>
            <div className="space-y-2">
              {proposals.map((p) => (
                <div
                  key={p.user.user_id}
                  className="flex items-center justify-between gap-3 p-3 bg-black/30 rounded-lg border border-gray-800"
                  data-testid={`proposal-${p.user.user_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium truncate">{p.user.artist_name}</span>
                      {confidenceBadge(p.confidence)}
                    </div>
                    <p className="text-xs text-gray-400 truncate">
                      Signup {formatDate(p.user.joined_at)} ({p.user.signup_method || "—"})
                      {" → matched referrer "}
                      <span className="text-emerald-300">{p.proposed_referrer.artist_name}</span>
                      {" "}({p.proposed_referrer.click_count_in_window} click(s))
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => acceptProposal(p)}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid={`accept-proposal-${p.user.user_id}`}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setProposals((prev) => prev.filter((x) => x.user.user_id !== p.user.user_id))}
                      className="border-gray-700 text-gray-300"
                      data-testid={`reject-proposal-${p.user.user_id}`}
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                filter === f.key
                  ? "bg-purple-600/30 border-purple-400 text-white"
                  : "bg-black/40 border-gray-700 text-gray-300 hover:border-purple-500/60"
              }`}
              data-testid={f.testId}
            >
              {f.label}
            </button>
          ))}
          <div className="flex-1 min-w-[240px]">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by artist, email, user_id, or referral code…"
              className="bg-black/40 border-gray-700 text-white"
              data-testid="referral-search-input"
              onKeyDown={(e) => { if (e.key === "Enter") fetchRows(); }}
            />
          </div>
          <Button onClick={fetchRows} disabled={loading} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="referral-search-btn">
            {loading ? "…" : "Search"}
          </Button>
        </div>

        {/* Results table */}
        <Card className="p-0 bg-gray-900/40 border-gray-800 overflow-hidden" data-testid="referral-rows-card">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No users match this filter.</div>
          ) : (
            <div className="divide-y divide-gray-800">
              {rows.map((r) => (
                <div key={r.user_id} className="p-3" data-testid={`referral-row-${r.user_id}`}>
                  <div className="flex items-center gap-3">
                    <img
                      src={getProfilePhotoUrl(r)}
                      alt={r.artist_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{r.artist_name}</div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {r.email || "—"} · {r.signup_method || "—"} · joined {formatDate(r.joined_at)}
                      </div>
                    </div>
                    <div className="text-right hidden sm:block min-w-[180px]">
                      {r.referred_by ? (
                        <div>
                          <div className="text-xs text-gray-400">Referred by</div>
                          <div className="text-sm text-emerald-300 font-medium truncate">{r.referrer_name || r.referred_by}</div>
                          <div className="text-[10px] text-gray-500">via {r.referral_source || "—"}</div>
                        </div>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40">
                          No referrer
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => toggleDiagnose(r.user_id)}
                        variant="outline"
                        className="text-amber-200 border-amber-500/40 hover:bg-amber-500/10"
                        data-testid={`diagnose-btn-${r.user_id}`}
                        title="Why does this user have / not have a referrer?"
                      >
                        {diagnoseByUser[r.user_id] ? "Hide why" : "Why?"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => toggleAudit(r.user_id)}
                        variant="outline"
                        className="text-gray-300 border-gray-700"
                        data-testid={`view-audit-${r.user_id}`}
                      >
                        {auditByUser[r.user_id] ? "Hide log" : "Log"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openAssign(r)}
                        className="bg-purple-600 hover:bg-purple-500 text-white"
                        data-testid={`assign-btn-${r.user_id}`}
                      >
                        {r.referred_by ? "Reassign" : "Assign"}
                      </Button>
                    </div>
                  </div>
                  {diagnoseByUser[r.user_id] && (
                    <div className="mt-3 ml-12 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 space-y-2 text-xs" data-testid={`diagnose-${r.user_id}`}>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-300">
                        <span><span className="text-gray-500">Status:</span> <span className={diagnoseByUser[r.user_id].status === 'attributed' ? 'text-emerald-300 font-medium' : 'text-rose-300 font-medium'}>{diagnoseByUser[r.user_id].status}</span></span>
                        <span><span className="text-gray-500">Signup:</span> {diagnoseByUser[r.user_id].user.signup_method || '—'}</span>
                        <span><span className="text-gray-500">Joined:</span> {formatDate(diagnoseByUser[r.user_id].user.joined_at || diagnoseByUser[r.user_id].user.created_at)}</span>
                        <span><span className="text-gray-500">Nearby clicks:</span> {diagnoseByUser[r.user_id].nearby_clicks_count}</span>
                      </div>
                      {diagnoseByUser[r.user_id].current_referrer && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Current referrer:</span>{' '}
                          <span className="text-emerald-300">{diagnoseByUser[r.user_id].current_referrer.artist_name}</span>{' '}
                          <span className="text-gray-500">via code</span>{' '}
                          <code className="text-purple-300">{diagnoseByUser[r.user_id].current_referrer.referral_code}</code>
                        </div>
                      )}
                      {diagnoseByUser[r.user_id].reason && (
                        <div className="text-amber-100 leading-relaxed">
                          <span className="text-amber-300 font-semibold">Why: </span>
                          {diagnoseByUser[r.user_id].reason}
                        </div>
                      )}
                      {diagnoseByUser[r.user_id].nearby_referral_clicks?.length > 0 && (
                        <details className="text-gray-400">
                          <summary className="cursor-pointer text-gray-300 hover:text-white">
                            Show {diagnoseByUser[r.user_id].nearby_referral_clicks.length} click(s) in ±24h window
                          </summary>
                          <ul className="mt-1 space-y-0.5 ml-3">
                            {diagnoseByUser[r.user_id].nearby_referral_clicks.map((c, i) => (
                              <li key={i} className="font-mono text-[10px]">
                                {formatDate(c.created_at)} · code={c.referral_code} · ref={c.referrer_user_id} · ip={c.ip || '—'}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                  {auditByUser[r.user_id] && (
                    <div className="mt-3 ml-12 space-y-1 text-xs" data-testid={`audit-list-${r.user_id}`}>
                      {auditByUser[r.user_id].length === 0 ? (
                        <p className="text-gray-500">No audit history.</p>
                      ) : (
                        auditByUser[r.user_id].map((a) => (
                          <div key={a.audit_id} className="flex gap-2 text-gray-400">
                            <span className="text-gray-600 shrink-0">{formatDate(a.created_at)}</span>
                            <span className="text-purple-300 shrink-0">{a.action}</span>
                            <span className="truncate">
                              {a.new_referrer_name ? `→ ${a.new_referrer_name}` : ""}
                              {a.actor_name ? ` (by ${a.actor_name})` : ""}
                              {a.reason ? ` · "${a.reason}"` : ""}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Assign modal */}
      {assignTarget && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => !assigning && setAssignTarget(null)}
          data-testid="assign-modal"
        >
          <Card
            className="w-full max-w-md p-6 bg-[#161620] border border-purple-500/40"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-2">
              {assignTarget.referred_by ? "Reassign referrer" : "Assign referrer"}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              For <span className="text-white">{assignTarget.artist_name}</span>
              {assignTarget.referred_by && (
                <> · current: <span className="text-emerald-300">{assignTarget.referrer_name}</span></>
              )}
            </p>

            <div className="space-y-3">
              {/* Phase 15.5 P5 Batch F — autocomplete referrer search */}
              <div className="relative">
                <label className="text-xs text-gray-400 block mb-1">
                  Search referrer by name / username / email / code / user_id
                </label>
                {selectedReferrer ? (
                  <div
                    className="flex items-center gap-3 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/40"
                    data-testid="assign-selected-referrer"
                  >
                    <img
                      src={getProfilePhotoUrl(selectedReferrer)}
                      alt={selectedReferrer.artist_name}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">
                        {selectedReferrer.artist_name}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {selectedReferrer.email || "—"} · <code className="text-purple-300">{selectedReferrer.user_id}</code>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearPickedReferrer}
                      className="border-gray-700 text-gray-300"
                      data-testid="assign-clear-referrer"
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <>
                    <Input
                      value={referrerSearch}
                      onChange={(e) => setReferrerSearch(e.target.value)}
                      placeholder="Start typing an artist name…"
                      className="bg-black/40 border-gray-700 text-white"
                      data-testid="assign-referrer-search"
                      autoFocus
                    />
                    {(referrerSearchLoading || referrerResults.length > 0 || (referrerSearch.trim().length >= 2 && !referrerSearchLoading && referrerResults.length === 0)) && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-[#0e0e16] border border-purple-500/40 rounded-lg shadow-xl max-h-72 overflow-y-auto z-10"
                        data-testid="assign-referrer-dropdown"
                      >
                        {referrerSearchLoading ? (
                          <div className="p-3 text-xs text-gray-400">Searching…</div>
                        ) : referrerResults.length === 0 ? (
                          <div className="p-3 text-xs text-gray-400">
                            No match. Try a different spelling, or paste a referral_code / user_id below.
                          </div>
                        ) : (
                          referrerResults.map((u) => (
                            <button
                              key={u.user_id}
                              type="button"
                              onClick={() => pickReferrer(u)}
                              className="w-full text-left flex items-center gap-2 p-2 hover:bg-purple-500/10 border-b border-gray-800 last:border-b-0 transition-colors"
                              data-testid={`assign-referrer-result-${u.user_id}`}
                            >
                              <img
                                src={getProfilePhotoUrl(u)}
                                alt={u.artist_name}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-white text-sm truncate">{u.artist_name}</div>
                                <div className="text-[10px] text-gray-500 truncate">
                                  {u.email || "—"} · <code className="text-purple-300">{u.user_id}</code>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-300 select-none">
                  Power-user: paste referral_code or user_id
                </summary>
                <div className="mt-2">
                  <Input
                    value={refInput}
                    onChange={(e) => { setRefInput(e.target.value); setSelectedReferrer(null); }}
                    placeholder="ref_xxxxxxxx or user_xxxxxxxxxxxx"
                    className="bg-black/40 border-gray-700 text-white"
                    data-testid="assign-referrer-input"
                  />
                </div>
              </details>

              <div>
                <label className="text-xs text-gray-400 block mb-1">Reason / repair notes</label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Joined through Google OAuth before referral fix"
                  className="bg-black/40 border-gray-700 text-white"
                  data-testid="assign-reason-input"
                />
              </div>
              {assignTarget.referred_by && (
                <label className="flex items-center gap-2 text-xs text-amber-200">
                  <input
                    type="checkbox"
                    checked={forceReassign}
                    onChange={(e) => setForceReassign(e.target.checked)}
                    className="w-4 h-4 accent-amber-500"
                    data-testid="force-reassign-checkbox"
                  />
                  Confirm reassign — overwrites the current attribution
                </label>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <Button
                onClick={submitAssign}
                disabled={assigning}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid="assign-submit-btn"
              >
                {assigning ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={() => !assigning && setAssignTarget(null)}
                variant="outline"
                className="border-gray-700 text-white"
                data-testid="assign-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminReferralsPage;
